import { logOut } from "./authService";
import { isRateLimitError, extractRetryAfter, extractEndpoint, getRateLimitMessage } from "../utils/rateLimit";
import { getSecondsUntilExpiration, isTokenExpiredRecently } from "../utils/tokenExpiration";
import { triggerSessionWarningModal } from "../context/SessionWarningContext";

/**
 * Parse FastAPI / structured API errors: { detail: string | { code, message, errors? } }
 * or top-level { code, message }.
 */
export const extractApiErrorDetail = (error) => {
  if (!error?.response?.data) {
    return { code: null, message: null, fieldErrors: null };
  }
  const data = error.response.data;
  if (data?.code && data?.message != null && data?.detail == null) {
    return { code: data.code, message: data.message, fieldErrors: null };
  }
  const d = data?.detail;
  if (typeof d === "string") {
    return { code: null, message: d, fieldErrors: null };
  }
  if (d && typeof d === "object") {
    const code = "code" in d ? d.code : null;
    const message = "message" in d ? d.message : null;
    const fieldErrors =
      code === "EMPLOYEE_SCHEMA_VALIDATION" && d.errors && typeof d.errors === "object"
        ? d.errors
        : null;
    return { code, message, fieldErrors };
  }
  return { code: null, message: data?.message ?? null, fieldErrors: null };
};

const formatFieldErrors = (fieldErrors) => {
  if (!fieldErrors || typeof fieldErrors !== "object") return "";
  return Object.entries(fieldErrors)
    .map(([k, v]) => `${k}: ${v}`)
    .join("; ");
};

export const ErrorHandler = (error, useMessage = false) => {
	if (!error) return;
	
	if (isRateLimitError(error)) {
		const retryAfter = extractRetryAfter(error);
		const endpoint = extractEndpoint(error);
		const rateLimitInfo = getRateLimitMessage(endpoint, retryAfter);
		
		return {
			message: rateLimitInfo.message,
			retryAfter: rateLimitInfo.retryAfter,
			isRateLimited: true,
			endpoint,
		};
	}
	
	let message = "";
	let defaultMessage = "Something went wrong, try again please.";
	if (error?.response) {
	  if (error?.response?.status > 500 && error?.response?.status < 600) {
		return defaultMessage;
	  }
	  if (error.response.status === 401) {
		const WARNING_WINDOW = 60;
		
		const secondsUntilExpiration = getSecondsUntilExpiration();
		if (secondsUntilExpiration !== null && secondsUntilExpiration <= WARNING_WINDOW && secondsUntilExpiration >= 0) {
			console.log(`⚠️ 401 received but token expires in ${secondsUntilExpiration} seconds (within ${WARNING_WINDOW}s window). Letting SessionWarningModal handle it.`);
			return;
		}
		
		const expiredRecently = isTokenExpiredRecently(WARNING_WINDOW);
		if (expiredRecently === true) {
			triggerSessionWarningModal();
			return;
		} else if (expiredRecently === false) {
			logOut();
			return;
		} else {
			logOut();
			return;
		}
	  } else {
		const extracted = extractApiErrorDetail(error);
		if (extracted.message) {
		  message = extracted.message;
		}
		if (extracted.fieldErrors) {
		  const fe = formatFieldErrors(extracted.fieldErrors);
		  message = message ? `${message}. ${fe}` : fe;
		}
		if (!message) {
		message = error?.response?.data?.errors
		  ? Object.values(error?.response?.data?.errors)?.[0]?.[0]
		  : error?.response?.data?.validationMessages?.[0] ||
			error?.response?.data?.userMessage ||
			error?.response?.data?.message ||
			error?.response?.data?.msg ||
			error?.response?.data?.Message ||
			error?.response?.data?.error?.message ||
			error?.response?.data?.error ||
			error?.response?.data?.title ||
			error?.response?.result?.message ||
			error?.response?.result?.error ||
			defaultMessage;
		}
		if (useMessage) {
		  message = error.response.data.message || error.response.data.Message || message;
		}
	  }
	} else if (error?.request) {
	  message = error?.message;
	} else {
	  message = error?.message;
	}
	return message;
};
